/**
 * Traduce los scopes OIDC del token (p. ej. `procurement.receipt.read`) a algo
 * que un humano entienda. Un scope tiene la forma `area.recurso.accion`; se
 * traduce cada parte con un diccionario y se compone una etiqueta legible, con
 * respaldo genérico para scopes nuevos que aún no estén en la tabla (así un
 * permiso recién añadido en el backend no aparece como un identificador críptico
 * ni rompe la pantalla).
 */

export interface Permiso {
  /** El scope crudo, tal cual viene en el token. */
  scope: string;
  /** Clave del área (primer segmento): organization, cash, sales… */
  area: string;
  /** Área en español, para agrupar y encabezar. */
  areaLabel: string;
  /** Último segmento: read | write | bootstrap | admin | … */
  accion: string;
  /** Etiqueta legible completa (acción + recurso). */
  titulo: string;
}

export const AREAS: Record<string, string> = {
  organization: "Organización",
  catalog: "Catálogo",
  inventory: "Inventario",
  procurement: "Compras",
  sales: "Ventas",
  cash: "Caja y tesorería",
  payments: "Cartera",
  accounting: "Contabilidad",
  tax: "Impuestos",
  pricing: "Precios",
  electronic_documents: "Documentos DIAN",
};

/** Orden de presentación de las áreas; el resto va detrás, alfabético. */
export const ORDEN_AREAS = [
  "organization",
  "catalog",
  "inventory",
  "procurement",
  "sales",
  "cash",
  "payments",
  "accounting",
  "tax",
  "pricing",
  "electronic_documents",
];

const ACCIONES: Record<string, string> = {
  read: "Ver",
  write: "Gestionar",
  bootstrap: "Arranque inicial de",
  admin: "Administrar",
};

/**
 * Recurso en español, por `area.recurso`. Va con el área porque un mismo nombre
 * cambia de sentido según el módulo (un `receipt` de compras es una recepción de
 * mercancía; el de caja es un recibo).
 */
const RECURSOS: Record<string, string> = {
  "organization.tenant": "la organización (tenant)",
  "organization.legal_entity": "las entidades legales",
  "organization.topology": "sedes, bodegas y ubicaciones",
  "organization.authorization": "roles y permisos",
  "catalog.product": "productos",
  "catalog.third_party": "terceros (clientes y proveedores)",
  "inventory.availability": "disponibilidad de existencias",
  "inventory.lot": "lotes",
  "inventory.presentation_conversion_policy": "política de conversión de presentaciones",
  "procurement.purchase_order": "órdenes de compra",
  "procurement.invoice": "facturas de proveedor",
  "procurement.receipt": "recepciones de mercancía",
  "procurement.replenishment_policy": "política de reabastecimiento",
  "procurement.invoice_match_policy": "política de conciliación de facturas",
  "procurement.receipt_policy": "política de recepción",
  "sales.sale": "ventas",
  "sales.sale_policy": "política de ventas",
  "sales.sale_return_policy": "política de devoluciones",
  "cash.receipt": "recibos de caja",
  "cash.disbursement": "desembolsos",
  "cash.disbursement_policy": "política de desembolsos",
  "cash.withdrawal_policy": "política de retiros",
  "cash.session": "sesiones de caja",
  "payments.payment_policy": "política de pagos",
  "payments.sale_payment": "pagos de ventas",
  "accounting.master": "plan de cuentas",
  "accounting.journal": "asientos contables",
  "accounting.period": "periodos contables",
  "tax.rule": "reglas de impuestos",
  "pricing.rule": "reglas de precios",
  "electronic_documents.admin": "documentos electrónicos (DIAN)",
};

function capitalizar(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Traduce un scope suelto a un {@link Permiso} legible. */
export function describirPermiso(scope: string): Permiso {
  const partes = scope.split(".");
  const area = partes[0] ?? scope;
  const accion = partes[partes.length - 1] ?? "";
  const recursoKey = partes.slice(0, -1).join("."); // "area.recurso"
  const areaLabel = AREAS[area] ?? capitalizar(area.replace(/_/g, " "));
  const accionLabel = ACCIONES[accion] ?? capitalizar(accion);
  const recursoLabel =
    RECURSOS[recursoKey] ??
    partes.slice(1, -1).join(" ").replace(/_/g, " ") ??
    area;
  const titulo = `${accionLabel} ${recursoLabel || area}`.trim();
  return { scope, area, areaLabel, accion, titulo };
}

export interface GrupoPermisos {
  area: string;
  areaLabel: string;
  permisos: Permiso[];
}

/**
 * Traduce y agrupa una lista de scopes por área, en el orden de presentación.
 * Dentro de cada área, primero lo que solo se ve (read) y luego lo que se
 * gestiona (write), y a igualdad, por etiqueta.
 */
export function agruparPermisos(scopes: string[]): GrupoPermisos[] {
  const porArea = new Map<string, GrupoPermisos>();
  for (const scope of scopes) {
    const p = describirPermiso(scope);
    const grupo = porArea.get(p.area) ?? { area: p.area, areaLabel: p.areaLabel, permisos: [] };
    grupo.permisos.push(p);
    porArea.set(p.area, grupo);
  }
  const rango = (area: string) => {
    const i = ORDEN_AREAS.indexOf(area);
    return i === -1 ? ORDEN_AREAS.length : i;
  };
  const grupos = [...porArea.values()].sort(
    (a, b) => rango(a.area) - rango(b.area) || a.areaLabel.localeCompare(b.areaLabel),
  );
  for (const g of grupos) {
    g.permisos.sort(
      (a, b) =>
        Number(a.accion !== "read") - Number(b.accion !== "read") ||
        a.titulo.localeCompare(b.titulo),
    );
  }
  return grupos;
}
